/**
 * Pre-pack print-on-demand (POD) box labels — label SKUs that must not appear on kitting pick routes.
 *
 * Label types (box label, carrier box label, accessory box label) are paired at pre-pack by
 * scanning a physical label or printing on demand (hub box template). They are not warehouse
 * picks to the pre-pack floor bin.
 *
 * Physical cartons such as **88-406-24** (BOM `box` variable) are stocked at pre-pack via
 * `manufacturing.station_stock` and are not POD labels.
 *
 * Ops:
 * - **Scan mode:** use leftover kitted physical labels when available (88-011-*, 88-403-*, 88-013-*).
 * - **Print mode (POD):** hub renders ZPL onto the printer; no label SKU is consumed from station stock.
 */

/** Lowercase SKU prefixes for Megaventory "Box Label" product lines (see bomMegaventoryProductCategorize). */
export const PREPACK_POD_BOX_LABEL_SKU_PREFIXES_LOWER = [
  "88-011-",
  "88-403-",
  "88-013-",
] as const;

export function isPrepackPodBoxLabelSku(skuRaw: string): boolean {
  const sku = skuRaw.trim().toLowerCase();
  if (sku.length === 0) return false;
  return PREPACK_POD_BOX_LABEL_SKU_PREFIXES_LOWER.some((prefix) => sku.startsWith(prefix));
}

const prefixNotLikeClauses = PREPACK_POD_BOX_LABEL_SKU_PREFIXES_LOWER.map(
  (prefix) =>
    `lower(trim(public.build_order_bom.component_sku)) not like '${prefix.replace(/'/g, "''")}%'`,
).join("\n      and ");

const boxLabelSkuLowerSubquery = `(
    select lower(trim(public.sku.sku))
      from public.sku
      left join public.mv_product_category
        on public.mv_product_category.mv_category_id = public.sku.mv_category_id
     where lower(trim(coalesce(public.mv_product_category.mv_category_name, ''))) = 'label'
       and lower(trim(coalesce(public.sku.mv_description, ''))) = 'box label'
  )`;

/**
 * Exclude pre-pack POD box label BOM components from work-order incoming / kitting demand.
 * Uses a hashed NOT IN subquery so Postgres evaluates the box-label SKU set once (not per BOM row).
 */
export const SQL_WHERE_WO_DEMAND_COMPONENT_NOT_PREPACK_POD_BOX_LABEL = `(
      ${prefixNotLikeClauses}
      and lower(trim(public.build_order_bom.component_sku)) not in ${boxLabelSkuLowerSubquery}
)`;
